/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable react/no-array-index-key */
/* eslint-disable react/jsx-props-no-spreading */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Checkbox, Colors, Input, InputProps } from '..';
import '../common.module.css';
import * as Icons from '../Icons';
import * as Styled from './styles';
import Typography from '../Typography/Typography';

type MultiSelectDropDownOptionMode = 'include' | 'exclude';

interface MultiSelectDropDownOption {
  id?: string;
  label?: string;
  selected?: boolean;
  /**
   * Only with `selectionMode: 'include-exclude'`: whether the option is included, excluded or
   * (when `undefined`) left alone. `selected` mirrors it, being `true` for both marked states.
   */
  mode?: MultiSelectDropDownOptionMode;
  type?: 'option' | 'divider';
  isAllOption?: boolean;
  /**
   * Renders the option as a collapsible group header instead of a regular option. Its checkbox
   * selects/de-selects every option carrying the same `groupId`.
   */
  isGroupOption?: boolean;
  /** Ties an option to the group header option carrying the same `groupId`. */
  groupId?: string;
}

interface MultiSelectDropDownProps {
  input?: InputProps;
  options: MultiSelectDropDownOption[];
  onChange?: (options: MultiSelectDropDownOption[]) => void;
  title?: string;
  id?: string;
  includeContainerCard?: boolean; // being used inside another component
  collapsable?: boolean;
  collapsedByDefault?: boolean;
  onCollapseClick?: (isCollapsed?: boolean) => void;
  onShowMoreClick?: (id: string) => void;
  limit?: number;
  disableSearch?: boolean;
  disableScroll?: boolean;
  maxHeightInVH?: number;
  groupsCollapsedByDefault?: boolean;
  /** Rendered inside the dropdown card, above the title and search input (e.g. a mode toggle). */
  header?: React.ReactNode;
  /**
   * How an option is picked. With `'include-exclude'`, every option shows an include/exclude
   * button pair instead of a checkbox and carries a `mode` (clicking the active button clears it).
   * `selected` mirrors `mode` (true for both marked states), so that the chip label, the cross
   * button and `onChange` keep listing the options the user singled out.
   */
  selectionMode?: 'select' | 'include-exclude';
}

const isSelectableOption = (option: MultiSelectDropDownOption) =>
  !option.isAllOption && option.type !== 'divider' && !option.isGroupOption;

const selectOrDeselectAllOption = (
  updatedOptions: MultiSelectDropDownOption[],
  isVisible?: (option: MultiSelectDropDownOption) => boolean
) => {
  // if a regular option is being selected/de-selected, the all option (if it exists) needs to
  // get selected (if all options are selected) or de-selected (if one of the options gets de-selected)
  const isEveryOptionSelected = !updatedOptions.find(
    (option) =>
      isSelectableOption(option) &&
      !option.selected &&
      (!isVisible || isVisible(option))
  );
  return updatedOptions.map((option) =>
    option.isAllOption ? { ...option, selected: isEveryOptionSelected } : option
  );
};

const MultiSelectDropDown = ({
  input = {},
  options,
  onChange,
  title,
  id,
  collapsable = false,
  collapsedByDefault = false,
  onCollapseClick,
  onShowMoreClick,
  includeContainerCard = true,
  limit = 0,
  disableSearch = false,
  disableScroll = false,
  maxHeightInVH,
  groupsCollapsedByDefault = true,
  header,
  selectionMode = 'select',
}: MultiSelectDropDownProps) => {
  // A grouped list is already compacted by its collapsed groups, and slicing it would cut groups
  // in half, so the limit/"show more" behaviour does not apply to it
  const optionsLimit = options.some((option) => option.isGroupOption)
    ? 0
    : limit;

  const [inputText, setInputText] = useState('');
  const [currentOptions, setCurrentOptions] = useState(
    selectOrDeselectAllOption(options)
  );
  const [hoveredIndex, setHoveredIndex] = useState<number | undefined>();
  const [isCollapsed, setIsCollapsed] = useState(collapsedByDefault);
  const [collapsedGroups, setCollapsedGroups] = useState<
    Record<string, boolean>
  >({});
  // Only show the "show all" button if limit is 0 OR when there are no selected options whose index is greater than the limit
  const [shouldShowAllOptions, setShowAllOptions] = useState(
    () =>
      optionsLimit === 0 ||
      options
        .filter((_, i) => i >= optionsLimit)
        .find((option) => option.selected) !== undefined
  );
  const shouldShowSearch =
    (currentOptions.length > optionsLimit || optionsLimit === 0) &&
    !disableSearch &&
    !isCollapsed;

  const groupLabelById = useMemo(
    () =>
      new Map(
        options
          .filter((option) => option.isGroupOption && option.groupId)
          .map((option) => [option.groupId as string, option.label ?? ''])
      ),
    [options]
  );

  const matchesSearch = useCallback(
    (option: MultiSelectDropDownOption, text: string) => {
      if (!text) {
        return true;
      }
      const search = text.toLowerCase();
      if (option.label?.toLowerCase().includes(search)) {
        return true;
      }
      // grouped options are also matched by their group's label, so searching for e.g. "LATAM"
      // surfaces every option under the LATAM groups
      return !option.isGroupOption && option.groupId
        ? !!groupLabelById.get(option.groupId)?.toLowerCase().includes(search)
        : false;
    },
    [groupLabelById]
  );

  // Ensuring that when a parent changes the options, this component re-renders. Compared by
  // VALUE, not by identity: parents commonly rebuild this array on every render, and a parent
  // that only commits on save (the filter sidebar) never sees the selection in between - so
  // resetting on a new-but-equal array would throw away what the user just ticked.
  // The comparison is against the previous props rather than the local state, so a selection
  // made here is never mistaken for a parent update.
  const lastOptions = useRef(JSON.stringify(options));

  useEffect(() => {
    const serializedOptions = JSON.stringify(options);

    if (lastOptions.current === serializedOptions) {
      return;
    }

    lastOptions.current = serializedOptions;
    // The update is NOT reported back through `onChange`: echoing a parent-driven change makes it
    // look like the user picked it, and the parent then persists whatever this dropdown holds.
    setCurrentOptions(selectOrDeselectAllOption(options));
  }, [options]);

  // Every selection made by the user goes through here, so that `onChange` is fired for those
  // and only those
  const applySelection = (updatedOptions: MultiSelectDropDownOption[]) => {
    setCurrentOptions(updatedOptions);
    onChange?.(updatedOptions);
  };

  useEffect(() => {
    setShowAllOptions(
      optionsLimit === 0 ||
        currentOptions
          .filter((_, i) => i >= optionsLimit)
          .find((option) => option.selected) !== undefined
    );
  }, [optionsLimit, currentOptions]);

  const handleOptionSelect = (
    index: number,
    value: boolean,
    isAllOption?: boolean
  ) => {
    let updatedOptions = isAllOption
      ? options.map((option) =>
          // group headers derive their state from their options, they are never selected themselves
          option.isGroupOption
            ? { ...option, selected: false }
            : !matchesSearch(option, inputText) && !option.isAllOption
            ? option
            : { ...option, selected: value }
        )
      : currentOptions.map((option, i) =>
          i === index ? { ...option, selected: value } : option
        );

    if (!isAllOption) {
      updatedOptions = selectOrDeselectAllOption(updatedOptions);
    }

    applySelection(updatedOptions);
  };

  const handleOptionModeChange = (
    index: number,
    mode?: MultiSelectDropDownOptionMode
  ) => {
    applySelection(
      currentOptions.map((option, i) =>
        i === index ? { ...option, mode, selected: mode !== undefined } : option
      )
    );
  };

  const handleGroupSelect = (groupId: string, value: boolean) => {
    applySelection(
      selectOrDeselectAllOption(
        currentOptions.map((option) =>
          !option.isGroupOption &&
          option.groupId === groupId &&
          matchesSearch(option, inputText)
            ? { ...option, selected: value }
            : option
        )
      )
    );
  };

  const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newOptions = e.target.value
      ? options.map((o) => {
          const current = currentOptions.filter((c) => c.id === o.id)?.[0];
          return {
            ...o,
            ...(selectionMode === 'include-exclude'
              ? { mode: current?.mode }
              : {}),
            selected: (o.isAllOption ? false : current?.selected) ?? false,
          };
        })
      : currentOptions;
    newOptions = selectOrDeselectAllOption(newOptions, (option) =>
      matchesSearch(option, e.target.value)
    );
    setCurrentOptions(newOptions);
    setInputText(e.target.value);
    setShowAllOptions(!!e.target.value);
  };

  const getGroupOptions = (groupId?: string) =>
    groupId
      ? currentOptions.filter(
          (option) =>
            !option.isGroupOption &&
            option.groupId === groupId &&
            matchesSearch(option, inputText)
        )
      : [];

  // While searching every matching group is expanded, so that matches are never hidden away
  const isGroupCollapsed = (groupId?: string) =>
    !inputText && (collapsedGroups[groupId ?? ''] ?? groupsCollapsedByDefault);

  const toggleGroup = (groupId?: string) => {
    if (!groupId) {
      return;
    }
    setCollapsedGroups((collapsed) => ({
      ...collapsed,
      [groupId]: !(collapsed[groupId] ?? groupsCollapsedByDefault),
    }));
  };

  // Options are kept paired with their index in `currentOptions` so that selecting one still
  // targets the right entry after the hidden ones are filtered out
  const displayedOptions = (
    optionsLimit && !shouldShowAllOptions && !inputText
      ? currentOptions.slice(0, optionsLimit)
      : currentOptions
  )
    .map((option, index) => ({ option, index }))
    .filter(({ option }) => {
      if (option.type === 'divider') {
        return !inputText;
      }
      if (option.isAllOption) {
        return true;
      }
      if (option.isGroupOption) {
        return getGroupOptions(option.groupId).length > 0;
      }
      if (!matchesSearch(option, inputText)) {
        return false;
      }
      return !(option.groupId && isGroupCollapsed(option.groupId));
    });

  const hasMatches = currentOptions.some((option) =>
    matchesSearch(option, inputText)
  );

  return (
    <Styled.Root
      data-test="multi-select-drop-down-container"
      $includeContainerCard={includeContainerCard}
      $disableScroll={disableScroll}
      $maxHeightInVH={maxHeightInVH}
    >
      <Styled.InputContainer
        data-test="multi-select-drop-down-search"
        $includeContainerCard={includeContainerCard}
      >
        {header ? (
          <Styled.Header data-test="multi-select-drop-down-header">
            {header}
          </Styled.Header>
        ) : (
          ''
        )}
        {collapsable ? (
          <Styled.IconsContainer
            role="button"
            tabIndex={0}
            onClick={() => {
              setIsCollapsed(!isCollapsed);
              if (onCollapseClick) onCollapseClick(!isCollapsed);
            }}
            onKeyDown={() => {
              setIsCollapsed(!isCollapsed);
              if (onCollapseClick) onCollapseClick(!isCollapsed);
            }}
          >
            {isCollapsed ? (
              <Icons.BaseIcons.Plus
                color={Colors.Primary[700]}
                dimension={12}
              />
            ) : (
              <Icons.BaseIcons.Minus
                color={Colors.Primary[700]}
                dimension={12}
              />
            )}{' '}
            {title}
          </Styled.IconsContainer>
        ) : title ? (
          <Styled.TypographyNoMargin type="text-sm" fontWeight="semi-bold">
            {title}
          </Styled.TypographyNoMargin>
        ) : (
          ''
        )}
        {shouldShowSearch ? (
          <Input
            {...input}
            fullWidth
            value={inputText}
            onChange={(e) => handleChangeSearch(e)}
          />
        ) : (
          ''
        )}
      </Styled.InputContainer>
      {!isCollapsed &&
        displayedOptions.map(({ option, index }) => {
          if (option.type === 'divider') {
            return <Styled.Divider key={`divider-${index}`} />;
          }

          if (option.isGroupOption) {
            const groupOptions = getGroupOptions(option.groupId);
            const selectedCount = groupOptions.filter(
              (groupOption) => groupOption.selected
            ).length;
            const isCollapsedGroup = isGroupCollapsed(option.groupId);

            return (
              <Styled.GroupOption
                data-test="multi-select-drop-down-group"
                key={`group-${option.groupId ?? index}`}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(undefined)}
              >
                <Styled.GroupCollapseButton
                  data-test="multi-select-drop-down-group-toggle"
                  role="button"
                  tabIndex={0}
                  aria-expanded={!isCollapsedGroup}
                  onClick={() => toggleGroup(option.groupId)}
                  onKeyDown={() => toggleGroup(option.groupId)}
                >
                  {isCollapsedGroup ? (
                    <Icons.BaseIcons.ChevronRight
                      color={Colors.Gray[500]}
                      dimension={16}
                    />
                  ) : (
                    <Icons.BaseIcons.ChevronDown
                      color={Colors.Gray[500]}
                      dimension={16}
                    />
                  )}
                </Styled.GroupCollapseButton>
                <Checkbox
                  checked={selectedCount === groupOptions.length}
                  indeterminate={
                    selectedCount > 0 && selectedCount < groupOptions.length
                  }
                  onClick={(c) => handleGroupSelect(option.groupId ?? '', c)}
                  label={option.label}
                  addHoverStyle={hoveredIndex === index}
                />
                <Styled.GroupCount data-test="multi-select-drop-down-group-count">
                  {selectedCount > 0
                    ? `${selectedCount}/${groupOptions.length}`
                    : groupOptions.length}
                </Styled.GroupCount>
              </Styled.GroupOption>
            );
          }

          if (option.selected === undefined || option.label === undefined) {
            throw new Error('Invalid option');
          }

          if (selectionMode === 'include-exclude' && !option.isAllOption) {
            const isIncluded = option.mode === 'include';
            const isExcluded = option.mode === 'exclude';
            return (
              <Styled.IncludeExcludeOption
                data-test="multi-select-drop-down-option"
                key={`${option.label}-${index}`}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(undefined)}
              >
                <Styled.IncludeExcludeLabel
                  $excluded={isExcluded}
                  $included={isIncluded}
                >
                  {option.label}
                </Styled.IncludeExcludeLabel>
                <Styled.IncludeExcludeButtons>
                  <Styled.IncludeExcludeButton
                    type="button"
                    data-test="multi-select-drop-down-include-button"
                    aria-label={`Include ${option.label}`}
                    aria-pressed={isIncluded}
                    title={isIncluded ? 'Clear' : 'Include'}
                    $active={isIncluded}
                    $activeBackground={Colors.Success[50]}
                    onClick={() =>
                      handleOptionModeChange(
                        index,
                        isIncluded ? undefined : 'include'
                      )
                    }
                  >
                    <Icons.BaseIcons.CheckCircle
                      color={
                        isIncluded ? Colors.Success[600] : Colors.Gray[300]
                      }
                      dimension={18}
                    />
                  </Styled.IncludeExcludeButton>
                  <Styled.IncludeExcludeButton
                    type="button"
                    data-test="multi-select-drop-down-exclude-button"
                    aria-label={`Exclude ${option.label}`}
                    aria-pressed={isExcluded}
                    title={isExcluded ? 'Clear' : 'Exclude'}
                    $active={isExcluded}
                    $activeBackground={Colors.Error[50]}
                    onClick={() =>
                      handleOptionModeChange(
                        index,
                        isExcluded ? undefined : 'exclude'
                      )
                    }
                  >
                    <Icons.BaseIcons.MinusCircle
                      color={isExcluded ? Colors.Error[600] : Colors.Gray[300]}
                      dimension={18}
                    />
                  </Styled.IncludeExcludeButton>
                </Styled.IncludeExcludeButtons>
              </Styled.IncludeExcludeOption>
            );
          }

          const OptionContainer = option.groupId
            ? Styled.GroupedOption
            : Styled.Option;

          return (
            <OptionContainer
              data-test="multi-select-drop-down-option"
              key={`${option.label}-${index}`}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(undefined)}
            >
              <Checkbox
                checked={option.selected}
                onClick={(c) =>
                  handleOptionSelect(index, c, option.isAllOption)
                }
                label={option.label}
                addHoverStyle={hoveredIndex === index}
              />
            </OptionContainer>
          );
        })}
      {!inputText &&
      !isCollapsed &&
      !shouldShowAllOptions &&
      currentOptions.length > optionsLimit ? (
        <Styled.ShowMoreOption
          data-test="show-more-button"
          role="button"
          tabIndex={0}
          onClick={() => {
            setShowAllOptions(true);
            onShowMoreClick?.((id ?? title) || '');
          }}
          onKeyDown={() => {
            setShowAllOptions(true);
            onShowMoreClick?.((id ?? title) || '');
          }}
        >{`Show ${
          currentOptions.length - optionsLimit
        } more`}</Styled.ShowMoreOption>
      ) : (
        ''
      )}
      {inputText && !hasMatches && (
        <Styled.NoMatches data-test="multi-select-drop-down-no-matches">
          <Typography type="text-sm" style={{ marginTop: 0 }}>
            No matches found
          </Typography>
        </Styled.NoMatches>
      )}
    </Styled.Root>
  );
};

export default MultiSelectDropDown;
export type {
  MultiSelectDropDownOption,
  MultiSelectDropDownOptionMode,
  MultiSelectDropDownProps,
};
